import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Users,
  Building2,
  Package,
  ToggleLeft,
  BarChart3,
  Database,
  HelpCircle,
  LogOut,
  X,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'

const navItems = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { to: '/packages', labelKey: 'nav.packages', icon: Package },
  { to: '/users', labelKey: 'nav.users', icon: Users },
  { to: '/businesses', labelKey: 'nav.businesses', icon: Building2 },
  { to: '/features', labelKey: 'nav.features', icon: ToggleLeft },
  { to: '/analytics', labelKey: 'nav.analytics', icon: BarChart3 },
  { to: '/documents', labelKey: 'nav.documents', icon: Database },
  { to: '/help', labelKey: 'nav.help', icon: HelpCircle },
]

interface SidebarProps {
  open: boolean
  onClose: () => void
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const { signOut, userProfile } = useAuth()
  const { t } = useTranslation()

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-white border-r border-gray-200 transition-transform duration-200 lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-gray-200 px-6">
          <h1 className="text-lg font-bold text-primary-600">{t('app.name')}</h1>
          <button onClick={onClose} aria-label={t('common.close')} className="lg:hidden text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                )
              }
            >
              <item.icon className="h-5 w-5" />
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-gray-200 p-4">
          {userProfile && (
            <div className="mb-3 px-3">
              <p className="text-sm font-medium text-gray-900">
                {userProfile.displayName}
              </p>
              <p className="truncate text-xs text-gray-500">{userProfile.email}</p>
            </div>
          )}
          <button
            onClick={signOut}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <LogOut className="h-5 w-5" />
            {t('nav.signOut')}
          </button>
        </div>
      </aside>
    </>
  )
}
